import {
  getLastLoginTenantSlug,
  getLastLoginTenantId,
} from 'shared/utils/lastTenantLogin';
import { getSessionProfile } from 'shared/utils/sessionProfileStore';
import {
  getTenantIdFromWindowLocation,
  isTenantRouteKey,
  isTenantUuid,
  resolveTenantPublicPathKey,
} from 'shared/utils/tenantRoute';

/**
 * Active tenant slug for URL prefixes (e.g. "datahouse").
 */
export function resolveTenantSlug(explicit) {
  if (explicit != null && String(explicit).trim() !== '') {
    const key = resolveTenantPublicPathKey({
      name: explicit,
      id: explicit,
      fallback: explicit,
    });
    if (key && isTenantRouteKey(key)) {
      return isTenantUuid(key) ? key : key;
    }
  }

  const profile = getSessionProfile();
  const fromProfile = resolveTenantPublicPathKey({
    name: profile?.tenantBranding?.name,
    id: profile?.tenantId,
  });
  if (fromProfile && isTenantRouteKey(fromProfile)) {
    return fromProfile;
  }

  const fromRoute = getTenantIdFromWindowLocation();
  if (fromRoute && isTenantRouteKey(fromRoute)) {
    return fromRoute;
  }

  const fromSlug = getLastLoginTenantSlug();
  if (fromSlug && isTenantRouteKey(fromSlug)) {
    return fromSlug;
  }

  const fromId = getLastLoginTenantId();
  return fromId || null;
}

/**
 * Build an in-app path that keeps the tenant name in the URL.
 * buildTenantAppPath('/dashboard') → '/datahouse/dashboard'
 * buildTenantAppPath('users') → '/datahouse/users'
 */
export function buildTenantAppPath(appPath, tenantSlug) {
  const slug = resolveTenantSlug(tenantSlug);
  const raw = appPath == null ? '' : String(appPath).trim();
  if (!raw || raw === '/') {
    return slug ? `/${encodeURIComponent(slug)}` : '/login';
  }

  // Already tenant-prefixed
  if (slug) {
    const encoded = encodeURIComponent(slug);
    const prefix = `/${encoded}`;
    const prefixRaw = `/${slug}`;
    if (
      raw === prefix ||
      raw === prefixRaw ||
      raw.startsWith(`${prefix}/`) ||
      raw.startsWith(`${prefixRaw}/`)
    ) {
      return raw.startsWith('/') ? raw : `/${raw}`;
    }
  }

  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (!slug) {
    return normalized;
  }
  return `/${encodeURIComponent(slug)}${normalized}`;
}

/**
 * Strip /{tenantSlug} from pathname for menu highlighting / legacy matching.
 * /datahouse/dashboard → /dashboard
 */
export function stripTenantPrefixFromPathname(pathname) {
  if (!pathname || pathname === '/') return pathname || '/';
  const parts = String(pathname).split('/').filter(Boolean);
  if (parts.length === 0) return '/';
  const first = decodeURIComponent(parts[0]);
  if (!isTenantRouteKey(first)) {
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  }
  if (parts.length === 1) {
    return '/';
  }
  return `/${parts.slice(1).map((p) => encodeURIComponent(decodeURIComponent(p))).join('/')}`;
}
